import { APP_NAME } from '../constants/index.js';

/**
 * Что написано на экране регистрации и под чем человек ставит галочки (§14).
 *
 * Тексты и ссылки — не разметка, а содержание, за которое отвечает владелец
 * платформы: это юридические документы, они меняются, и менять их должна Дарья,
 * не разработчик. Поэтому они хранятся настройкой и правятся в администрировании.
 *
 * Значения ниже — то, что Дарья передала. Они не записаны в базу: если настройки
 * нет, берутся эти. Так тексты приезжают с релизом, без правки боевой базы, а
 * первая же правка в администрировании их перекрывает.
 */

/** Слово или несколько слов внутри согласия, ведущие на документ. */
export type ConsentLink = {
  /** Текст, который станет ссылкой. Ищется в тексте согласия как есть. */
  label: string;
  url: string;
};

export type ConsentSetting = {
  id: string;
  /** Предложение целиком, как его читает человек. */
  text: string;
  /**
   * Ссылки внутри этого предложения.
   *
   * Ссылки описаны отдельно от текста, а не разметкой внутри него: Дарья
   * правит их в администрировании, и требовать от неё markdown значило бы
   * поставить между ней и текстом лишнее знание. Предложение остаётся
   * предложением, а рядом список — какие слова куда ведут.
   */
  links: ConsentLink[];
  /**
   * Без обязательного согласия регистрация не проходит. Необязательное
   * человек ставит по желанию: отказ от рассылок не мешает пользоваться
   * платформой (концепция v0.3, п. 5).
   */
  required: boolean;
  /**
   * Редакция документа, под которым ставится галочка, — например «от
   * 12.10.2026». Записывается вместе с согласием: через год важно знать не
   * только что человек согласился, но и с каким текстом.
   */
  revision: string;
};

export type RegistrationFormSettings = {
  /** Надпись над формой. */
  heading: string;
  subheading: string;
  consents: ConsentSetting[];
};

export const DEFAULT_REGISTRATION_FORM: RegistrationFormSettings = {
  heading: APP_NAME,
  subheading: 'Авторские игры, сессии с метафорическими картами, обучение',
  consents: [
    {
      id: 'terms',
      text: 'Я ознакомлен(а) с политикой, офертой, и даю согласие на обработку персональных данных',
      links: [
        { label: 'политикой', url: 'http://creativitas.ru/politika' },
        { label: 'офертой', url: 'http://creativitas.ru/oferta' },
        { label: 'обработку персональных данных', url: 'http://creativitas.ru/soglasie' },
      ],
      required: true,
      revision: '',
    },
    {
      id: 'newsletter',
      text: 'Я даю согласие на получение информационных рассылок',
      links: [
        { label: 'информационных рассылок', url: 'http://creativitas.ru/soglasieinfo' },
      ],
      required: false,
      revision: '',
    },
  ],
};

/** Согласие на рассылки: по нему строится список для рассылки и ссылка «Отписаться». */
export const NEWSLETTER_CONSENT_ID = 'newsletter';

/**
 * Настройка, сохранённая до разделения согласий, не знает ни `required`, ни
 * `revision`. Тогда обязательны все согласия, кроме рассылок: прежде
 * обязательными были оба, но согласие на рекламу, данное под принуждением,
 * недействительно — поэтому рассылки становятся необязательными и у старой
 * настройки.
 */
export function normalizeRegistrationForm(raw: RegistrationFormSettings): RegistrationFormSettings {
  return {
    ...raw,
    consents: raw.consents.map((consent) => ({
      ...consent,
      links: consent.links ?? [],
      required:
        typeof consent.required === 'boolean'
          ? consent.required
          : consent.id !== NEWSLETTER_CONSENT_ID,
      revision: typeof consent.revision === 'string' ? consent.revision : '',
    })),
  };
}

/** Ключ настройки в `app_settings`. */
export const REGISTRATION_FORM_SETTING_KEY = 'registration.form';

/**
 * Разбивает текст согласия на куски: обычный текст и ссылки.
 *
 * Считает одинаково на сервере и в браузере, поэтому лежит здесь, а не в
 * компоненте: по этим же кускам тест проверяет, что все ссылки нашлись в
 * тексте. Ссылка, которую не нашли, — молча пропавший юридический документ,
 * поэтому такой случай виден: он возвращается отдельно.
 */
export type ConsentPiece =
  | { kind: 'text'; value: string }
  | { kind: 'link'; value: string; url: string };

export function splitConsent(consent: ConsentSetting): {
  pieces: ConsentPiece[];
  missing: ConsentLink[];
} {
  const missing: ConsentLink[] = [];
  let pieces: ConsentPiece[] = [{ kind: 'text', value: consent.text }];

  /*
    Ссылки применяются по очереди, каждая — к ещё не разобранным кускам текста.
    Так метка, попавшая внутрь другой метки, не разрежет уже готовую ссылку.
  */
  for (const link of consent.links) {
    if (!link.label) continue;
    let found = false;
    const next: ConsentPiece[] = [];

    for (const piece of pieces) {
      if (piece.kind !== 'text' || found) {
        next.push(piece);
        continue;
      }
      const at = piece.value.indexOf(link.label);
      if (at === -1) {
        next.push(piece);
        continue;
      }
      found = true;
      const before = piece.value.slice(0, at);
      const after = piece.value.slice(at + link.label.length);
      if (before) next.push({ kind: 'text', value: before });
      next.push({ kind: 'link', value: link.label, url: link.url });
      if (after) next.push({ kind: 'text', value: after });
    }

    pieces = next;
    if (!found) missing.push(link);
  }

  return { pieces, missing };
}
